import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, type Locale, isLocale } from '@arthelper/shared';

const STORAGE_KEY = 'preferred-locale';

interface UseLocaleReturn {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  isDetected: boolean;
}

export function useLocale(profileLocale?: string | null): UseLocaleReturn {
  const { i18n } = useTranslation();
  const explicitOverrideRef = useRef<Locale | null>(null);

  const stored = localStorage.getItem(STORAGE_KEY);
  const hasStoredPreference = stored !== null && isLocale(stored);

  // If the user explicitly set a locale via setLocale(), honour it until
  // the profile catches up (the mutation is async so profileLocale is stale
  // for a render cycle or two).
  if (explicitOverrideRef.current && profileLocale === explicitOverrideRef.current) {
    explicitOverrideRef.current = null;
  }

  const resolved: Locale = explicitOverrideRef.current
    ? explicitOverrideRef.current
    : profileLocale && isLocale(profileLocale)
      ? profileLocale
      : hasStoredPreference
        ? (stored as Locale)
        : isLocale(i18n.language)
          ? (i18n.language as Locale)
          : DEFAULT_LOCALE;

  const isDetected = !profileLocale && !hasStoredPreference && !explicitOverrideRef.current;

  useEffect(() => {
    if (i18n.language !== resolved) {
      i18n.changeLanguage(resolved);
    }
  }, [resolved, i18n]);

  const setLocale = (newLocale: Locale) => {
    explicitOverrideRef.current = newLocale;
    localStorage.setItem(STORAGE_KEY, newLocale);
    // Mark as an explicit user action so useProfileLocaleSync doesn't overwrite it
    // before the profile mutation has had a chance to sync back.
    localStorage.setItem(`${STORAGE_KEY}-pending`, newLocale);
    i18n.changeLanguage(newLocale);
  };

  return { locale: resolved, setLocale, isDetected };
}
